import { Button, Drawer, Flex, Skeleton, Switch, Typography } from "antd"
import React, { useEffect, useMemo, useState } from "react"
import { useTranslation } from "react-i18next"

import { getFormatDate } from "../../../utils/date/get-date-safe.ts"
import type { ProjectDocument } from "../../Project/types/project-documents.types.ts"
import { availableStatusOptions } from "../helpers/available-status-options.ts"
import { formatDocumentDate } from "../helpers/document-date.ts"
import { canRequestReview, canViewReview } from "../helpers/document-review-status.ts"
import { documentTypeIconLookup } from "../helpers/document-type-visuals.ts"
import {
  renderDocumentTypeDecoration,
  renderStatusDecoration
} from "../helpers/render-metadata-decoration.tsx"
import { resolveFolderBreadcrumb } from "../helpers/resolve-folder-breadcrumb.ts"
import type { DocumentTypeOption } from "../hooks/useDocumentTypeOptions.ts"
import type { StatusOption } from "../hooks/useStatusOptions.ts"
import { MetadataSelect } from "./MetadataSelect.tsx"
import { type FileItem } from "../types/file-item.types.ts"
import { type FolderParent } from "../types/folder.types.ts"

const { Text, Title } = Typography

export interface FileAlbumContext {
  id: string
  name: string
}

interface FileDetailsPanelProps {
  open: boolean
  onClose: () => void
  file: FileItem
  albumContext: FileAlbumContext | null
  onAddToAlbum?: () => void
  onRename?: () => void
  onMove?: () => void
  onDownload: () => void
  documentTypeOptions?: DocumentTypeOption[]
  documentTypesFailed?: boolean
  canEditClassification?: boolean
  onChangeDocumentType?: (value: string | null) => Promise<boolean>
  onLinkRecord?: () => void
  onViewRecord?: () => void
  onUploadNewVersion?: () => void
  onViewVersionHistory?: () => void
  statusOptions?: StatusOption[]
  statusesFailed?: boolean
  onChangeStatus?: (value: string | null) => Promise<boolean>
  onRequestReview?: () => void
  onViewReview?: () => void
  onViewComments?: () => void
  onViewActivity?: () => void
  onShare?: () => void
  onToggleCustomerVisibility?: (visible: boolean) => Promise<boolean>
  // Global Files only — every per-project surface already has this context
  // from the page it's on, so `file.projectInfo` is never populated there.
  onViewProject?: () => void
}

export function FileDetailsPanel({
  open,
  onClose,
  file,
  albumContext,
  onAddToAlbum,
  onRename,
  onMove,
  onDownload,
  documentTypeOptions = [],
  documentTypesFailed = false,
  canEditClassification = false,
  onChangeDocumentType,
  onLinkRecord,
  onViewRecord,
  onUploadNewVersion,
  onViewVersionHistory,
  statusOptions = [],
  statusesFailed = false,
  onChangeStatus,
  onRequestReview,
  onViewReview,
  onViewComments,
  onViewActivity,
  onShare,
  onToggleCustomerVisibility,
  onViewProject
}: FileDetailsPanelProps) {
  const { t } = useTranslation()
  const [locationPath, setLocationPath] = useState<FolderParent[] | null>(null)
  const [locationLoading, setLocationLoading] = useState(false)
  const [editingType, setEditingType] = useState(false)
  const [savingType, setSavingType] = useState(false)
  const [editingStatus, setEditingStatus] = useState(false)
  const [savingStatus, setSavingStatus] = useState(false)
  const [togglingVisibility, setTogglingVisibility] = useState(false)
  const documentTypeIcons = useMemo(
    () => documentTypeIconLookup(documentTypeOptions),
    [documentTypeOptions]
  )

  useEffect(() => {
    setEditingType(false)
    setEditingStatus(false)
  }, [file.key])

  useEffect(() => {
    if (!open) return
    if (!file.folderId) {
      setLocationPath([])
      return
    }
    let cancelled = false
    setLocationLoading(true)
    void resolveFolderBreadcrumb(file.folderId).then((path) => {
      if (cancelled) return
      setLocationPath(path)
      setLocationLoading(false)
    })
    return () => {
      cancelled = true
    }
  }, [open, file.folderId])

  const locationLabel = [
    t("files.sidebar.title"),
    ...(locationPath ?? []).map((f) => f.name)
  ].join(" / ")

  return (
    <Drawer
      title={t("fileViewer.detailsPanel.title")}
      open={open}
      onClose={onClose}
      width={340}
      getContainer={false}
      style={{ position: "absolute" }}
      maskClosable
    >
      <Flex vertical gap="middle">
        <Title level={5} className="!mb-0 break-all">
          {file.name}
        </Title>

        {(file.projectInfo || file.isCompanyScoped) && (
          <Flex vertical gap={4}>
            <Text type="secondary">{t("fileViewer.detailsPanel.workspace", "Location")}</Text>
            {file.projectInfo ? (
              <>
                <Flex gap={8} align="center" wrap="wrap">
                  <Text>
                    {file.projectInfo.is_project
                      ? t("fileViewer.detailsPanel.project")
                      : t("fileViewer.detailsPanel.lead")}
                    {" · "}
                    {file.projectInfo.uid}
                    {file.projectInfo.name ? ` · ${file.projectInfo.name}` : ""}
                  </Text>
                  {onViewProject && (
                    <Button type="link" size="small" className="!p-0" onClick={onViewProject}>
                      {t("fileViewer.detailsPanel.view")}
                    </Button>
                  )}
                </Flex>
                {file.projectInfo.customer_name && (
                  <Text type="secondary" className="text-xs">
                    {file.projectInfo.customer_name}
                  </Text>
                )}
              </>
            ) : (
              // A company-scoped document — no project/lead workspace at
              // all — is a first-class, valid state, not missing data.
              <Text>{t("fileViewer.detailsPanel.companyFiles", "Company Files")}</Text>
            )}
          </Flex>
        )}

        <Flex vertical gap={4}>
          <Text type="secondary">{t("fileViewer.detailsPanel.type")}</Text>
          <Text>
            {file.fileTypeLabel ?? file.mimeType ?? t("fileViewer.detailsPanel.unknown")}
          </Text>
        </Flex>

        <Flex vertical gap={4}>
          <Text type="secondary">{t("fileViewer.detailsPanel.size")}</Text>
          <Text>{file.formattedFileSize ?? t("fileViewer.detailsPanel.unknown")}</Text>
        </Flex>

        <Flex vertical gap={4}>
          <Text type="secondary">{t("fileViewer.detailsPanel.created")}</Text>
          <Text>{getFormatDate(file.created) ?? file.created}</Text>
        </Flex>

        <Flex vertical gap={4}>
          <Text type="secondary">{t("fileViewer.detailsPanel.modified")}</Text>
          <Text>{getFormatDate(file.updated) ?? file.updated}</Text>
        </Flex>

        {file.source === "document" && (
          <Flex vertical gap={4}>
            <Text type="secondary">{t("fileViewer.detailsPanel.documentDate")}</Text>
            <Text>
              {formatDocumentDate(file.documentDate) ?? t("fileViewer.detailsPanel.unknown")}
            </Text>
          </Flex>
        )}

        <Flex vertical gap={4}>
          <Text type="secondary">{t("fileViewer.detailsPanel.folder", "Folder")}</Text>
          {locationLoading ? (
            <Skeleton.Input active size="small" style={{ width: 180 }} />
          ) : (
            <Text ellipsis={{ tooltip: locationLabel }}>{locationLabel}</Text>
          )}
        </Flex>

        <Flex vertical gap={4}>
          <Text type="secondary">{t("fileViewer.detailsPanel.albums")}</Text>
          {albumContext ? (
            <Text>{albumContext.name}</Text>
          ) : (
            <Flex vertical gap={8} align="start">
              <Text type="secondary">{t("fileViewer.detailsPanel.noAlbums")}</Text>
              {onAddToAlbum && (
                <Button size="small" onClick={onAddToAlbum}>
                  {t("fileViewer.detailsPanel.addToAlbum")}
                </Button>
              )}
            </Flex>
          )}
        </Flex>

        <Flex vertical gap={4}>
          <Text type="secondary">{t("fileViewer.detailsPanel.documentType")}</Text>
          {editingType ? (
            <MetadataSelect
              autoFocus
              open
              disabled={savingType}
              value={file.documentType}
              options={documentTypeOptions}
              renderDecoration={(value) =>
                renderDocumentTypeDecoration(value, documentTypeIcons[value])
              }
              loadFailedText={
                documentTypesFailed ? t("files.documentType.loadFailed") : undefined
              }
              placeholder={t("fileViewer.detailsPanel.selectDocumentType")}
              className="w-full"
              onBlur={() => setEditingType(false)}
              onChange={async (value) => {
                setSavingType(true)
                await onChangeDocumentType?.(value)
                setSavingType(false)
                setEditingType(false)
              }}
            />
          ) : (
            <Flex gap={8} align="center" wrap="wrap">
              {file.documentType &&
                renderDocumentTypeDecoration(
                  file.documentType,
                  documentTypeIcons[file.documentType]
                )}
              <Text type={file.documentTypeLabel ? undefined : "secondary"}>
                {file.documentTypeLabel ?? t("fileViewer.detailsPanel.notAssigned")}
              </Text>
              {canEditClassification && onChangeDocumentType && (
                <Button
                  type="link"
                  size="small"
                  className="!p-0"
                  onClick={() => setEditingType(true)}
                >
                  {file.documentTypeLabel
                    ? t("fileViewer.detailsPanel.edit")
                    : t("fileViewer.detailsPanel.assign")}
                </Button>
              )}
            </Flex>
          )}
        </Flex>

        {(file.status || onChangeStatus) && (
          <Flex vertical gap={4}>
            <Text type="secondary">{t("fileViewer.detailsPanel.status")}</Text>
            {editingStatus ? (
              <MetadataSelect
                autoFocus
                open
                // The status workflow has no move back to "no status".
                allowClear={false}
                disabled={savingStatus}
                value={file.status}
                options={availableStatusOptions(statusOptions, file)}
                renderDecoration={renderStatusDecoration}
                loadFailedText={statusesFailed ? t("files.status.loadFailed") : undefined}
                placeholder={t("fileViewer.detailsPanel.selectStatus")}
                className="w-full"
                onBlur={() => setEditingStatus(false)}
                onChange={async (value) => {
                  setSavingStatus(true)
                  await onChangeStatus?.(value)
                  setSavingStatus(false)
                  setEditingStatus(false)
                }}
              />
            ) : (
              <Flex gap={8} align="center" wrap="wrap">
                {file.status && renderStatusDecoration(file.status)}
                <Text type={file.statusLabel ? undefined : "secondary"}>
                  {file.statusLabel ?? t("fileViewer.detailsPanel.notAssigned")}
                </Text>
                {canEditClassification && onChangeStatus && (
                  <Button
                    type="link"
                    size="small"
                    className="!p-0"
                    onClick={() => setEditingStatus(true)}
                  >
                    {t("fileViewer.detailsPanel.edit")}
                  </Button>
                )}
              </Flex>
            )}
          </Flex>
        )}

        {file.source === "document" && (
          <Flex vertical gap={4}>
            <Text type="secondary">{t("fileViewer.detailsPanel.relatedRecord")}</Text>
            <Flex gap={8} align="center" wrap="wrap">
              <Text type={file.relatedRecord ? undefined : "secondary"}>
                {file.relatedRecord?.label ?? t("fileViewer.detailsPanel.notLinked")}
              </Text>
              {file.relatedRecord && onViewRecord && (
                <Button type="link" size="small" className="!p-0" onClick={onViewRecord}>
                  {t("fileViewer.detailsPanel.view")}
                </Button>
              )}
              {canEditClassification && onLinkRecord && (
                <Button type="link" size="small" className="!p-0" onClick={onLinkRecord}>
                  {file.relatedRecord
                    ? t("fileViewer.detailsPanel.edit")
                    : t("fileViewer.detailsPanel.linkRecord")}
                </Button>
              )}
            </Flex>
          </Flex>
        )}

        {/* A company-scoped document (`file.isCompanyScoped`) has no
            customer to be visible to, so the toggle is suppressed rather
            than shown disabled-and-always-off. `isCompanyScoped` is the
            explicit signal for this (see its definition on `FileItem`) —
            unlike `projectInfo`, it is never ambiguous with "this caller
            didn't ask", so a per-project document (which never sets it)
            is unaffected. */}
        {file.source === "document" &&
          onToggleCustomerVisibility &&
          !file.isCompanyScoped && (
          <Flex vertical gap={4}>
            <Text type="secondary">{t("fileViewer.detailsPanel.customerVisibility")}</Text>
            <Flex gap={8} align="center" wrap="wrap">
              <Switch
                size="small"
                checked={!!(file.raw as ProjectDocument).is_visible_to_customer}
                loading={togglingVisibility}
                disabled={!canEditClassification}
                onChange={async (checked) => {
                  setTogglingVisibility(true)
                  await onToggleCustomerVisibility(checked)
                  setTogglingVisibility(false)
                }}
              />
              <Text type="secondary" className="text-xs">
                {(file.raw as ProjectDocument).is_visible_to_customer
                  ? t("fileViewer.detailsPanel.visibleToCustomer")
                  : t("fileViewer.detailsPanel.internalOnly")}
              </Text>
            </Flex>
          </Flex>
        )}

        {file.source === "document" && file.version !== undefined && (
          <Flex vertical gap={4}>
            <Text type="secondary">{t("fileViewer.detailsPanel.version")}</Text>
            <Flex gap={8} align="center" wrap="wrap">
              <Text>
                {t("fileViewer.detailsPanel.versionShort", { version: file.version })}
              </Text>
              {file.isCurrentVersion && (
                <Text type="secondary" className="text-xs">
                  · {t("files.versionHistory.current")}
                </Text>
              )}
              {onViewVersionHistory && (
                <Button
                  type="link"
                  size="small"
                  className="!p-0"
                  onClick={onViewVersionHistory}
                >
                  {t("fileViewer.detailsPanel.viewVersionHistory")}
                </Button>
              )}
            </Flex>
            {canEditClassification && onUploadNewVersion && (
              <Button size="small" className="self-start" onClick={onUploadNewVersion}>
                {t("files.uploadNewVersion.action")}
              </Button>
            )}
          </Flex>
        )}

        {file.source === "document" &&
          (onRequestReview || onViewReview || onViewComments || onViewActivity) && (
            <Flex vertical gap={8}>
              <Text type="secondary">{t("files.review.panelTitle")}</Text>
              <Flex gap="small" wrap="wrap">
                {canEditClassification &&
                  onRequestReview &&
                  canRequestReview(file.status) && (
                    <Button size="small" onClick={onRequestReview}>
                      {t("files.review.requestAction")}
                    </Button>
                  )}
                {onViewReview && canViewReview(file.status) && (
                  <Button size="small" onClick={onViewReview}>
                    {t("files.review.viewAction")}
                  </Button>
                )}
                {onViewComments && (
                  <Button size="small" onClick={onViewComments}>
                    {t("files.comments.viewAction")}
                  </Button>
                )}
                {onViewActivity && (
                  <Button size="small" onClick={onViewActivity}>
                    {t("files.activity.viewAction")}
                  </Button>
                )}
              </Flex>
            </Flex>
          )}

        {file.createdByName && (
          <Flex vertical gap={4}>
            <Text type="secondary">{t("fileViewer.detailsPanel.owner")}</Text>
            <Text>{file.createdByName}</Text>
          </Flex>
        )}

        <Flex gap="small" wrap="wrap">
          {onRename && (
            <Button size="small" onClick={onRename}>
              {t("files.actions.rename")}
            </Button>
          )}
          {onMove && (
            <Button size="small" onClick={onMove}>
              {t("files.actions.move")}
            </Button>
          )}
          <Button size="small" onClick={onDownload}>
            {t("fileViewer.download")}
          </Button>
          {file.canShare && onShare && (
            <Button size="small" onClick={onShare}>
              {t("files.actions.share")}
            </Button>
          )}
        </Flex>
      </Flex>
    </Drawer>
  )
}
